import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Save, Layers, EyeOff, Plus, RefreshCw } from 'lucide-react';

const HIDDEN = '__hidden__';

/** Maps each Zoho Books invoice category into a technology bucket used across trainer reports. */
export default function CategoryMaster({ isAdmin = false }) {
  const [d, setD] = useState(null);
  const [map, setMap] = useState({});
  const [courses, setCourses] = useState({});
  const [busy, setBusy] = useState(false);
  const [newBucket, setNewBucket] = useState('');

  const load = (fresh) => {
    api.get('/training/categories', { params: fresh ? { fresh: 1 } : {} })
      .then((r) => { setD(r.data); setMap(r.data.map); setCourses({}); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load the categories'));
  };
  useEffect(() => { load(false); }, []);

  const buckets = useMemo(() => {
    const live = new Set([...(d?.buckets || []), ...Object.values(map).filter((b) => b && b !== HIDDEN)]);
    if (newBucket.trim()) live.add(newBucket.trim());
    return [...live].sort();
  }, [d, map, newBucket]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put('/training/categories', { map, courses });
      setD(r.data); setMap(r.data.map); setCourses({});
      toast.success('Buckets saved — every trainer report follows them now');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="flex items-center justify-center py-14 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>;

  const counts = d.bucket_courses || {};
  return (
    <div className="space-y-6" data-testid="category-master">
      <div className="flex flex-wrap items-start gap-3">
        <Layers className="w-5 h-5 text-violet-600 mt-0.5" />
        <div className="flex-1 min-w-[280px]">
          <h3 className="text-sm font-semibold text-slate-800">Technology buckets</h3>
          <p className="text-[11.5px] text-slate-500 mt-0.5">
            The category is the Zoho Books category on the invoice — the same field the Revenue Report groups by.
            Books sends near-duplicates, so each one rolls into a bucket here. A course then inherits its bucket from the
            invoices of the students in its batches, which is what groups the trainers in every report.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => load(true)} data-testid="category-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Recalculate from invoices
        </Button>
        {isAdmin && (
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={save} disabled={busy} data-testid="category-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}Save buckets
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="category-bucket-chips">
        {buckets.map((b) => (
          <span key={b} className="px-2.5 py-1 rounded-full bg-violet-50 border border-violet-200 text-[11.5px] font-semibold text-violet-700">
            {b}<span className="ml-1.5 text-violet-400 font-normal">{counts[b] || 0} course(s)</span>
          </span>
        ))}
        {isAdmin && (
          <span className="inline-flex items-center gap-1">
            <Input value={newBucket} onChange={(e) => setNewBucket(e.target.value)} placeholder="New bucket name"
              className="h-7 w-40 text-[12px]" data-testid="category-new-bucket" />
            <Plus className="w-3.5 h-3.5 text-slate-400" />
          </span>
        )}
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="px-4 py-2.5 text-left">Books category (from the invoice)</th>
            <th className="px-4 py-2.5 text-left w-64">Rolls into</th>
          </tr></thead>
          <tbody>
            {Object.keys(map).sort().map((c) => (
              <tr key={c} className="border-b border-slate-100 last:border-0" data-testid={`category-row-${c}`}>
                <td className="px-4 py-2 font-medium text-slate-800">
                  {c}
                  {map[c] === HIDDEN && <EyeOff className="w-3 h-3 inline ml-1.5 text-slate-400" />}
                  {!d.live.includes(c) && <span className="ml-1.5 text-[10px] text-slate-400">(not seen on any invoice)</span>}
                </td>
                <td className="px-4 py-1.5">
                  <Select value={map[c]} onValueChange={(v) => setMap((p) => ({ ...p, [c]: v }))} disabled={!isAdmin}>
                    <SelectTrigger className="h-8 text-[12px]" data-testid={`category-select-${c}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {buckets.map((b) => <SelectItem key={b} value={b} className="text-[12px]">{b}</SelectItem>)}
                      <SelectItem value={HIDDEN} className="text-[12px] text-slate-400">— Hide from trainer reports —</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-800">Courses</h3>
        <p className="text-[11.5px] text-slate-500 mt-0.5 mb-2">
          Worked out from the invoices of the students in each course's batches. Courses with no invoice yet show as
          <b> Unmapped</b> — set those by hand and your choice always wins.
        </p>
        <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[420px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0"><tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-4 py-2.5 text-left">Course (from batches)</th>
              <th className="px-4 py-2.5 text-left w-64">Bucket</th>
            </tr></thead>
            <tbody>
              {(d.courses || []).map((row) => {
                const val = courses[row.course] ?? row.bucket;
                const unmapped = val === 'Unmapped';
                return (
                  <tr key={row.course} className={`border-b border-slate-100 last:border-0 ${unmapped ? 'bg-amber-50/50' : ''}`} data-testid={`category-course-${row.course}`}>
                    <td className="px-4 py-2 font-medium text-slate-800">
                      {row.course}
                      {row.manual && <span className="ml-1.5 text-[10px] text-violet-600">set by hand</span>}
                    </td>
                    <td className="px-4 py-1.5">
                      <Select value={val} onValueChange={(v) => setCourses((p) => ({ ...p, [row.course]: v }))} disabled={!isAdmin}>
                        <SelectTrigger className={`h-8 text-[12px] ${unmapped ? 'border-amber-300' : ''}`} data-testid={`category-course-select-${row.course}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {buckets.map((b) => <SelectItem key={b} value={b} className="text-[12px]">{b}</SelectItem>)}
                          <SelectItem value="Unmapped" className="text-[12px] text-amber-600">Unmapped</SelectItem>
                          <SelectItem value={HIDDEN} className="text-[12px] text-slate-400">— Hide from trainer reports —</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
